import { SelectionProviderChildProps } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../gen-src/Localize';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FieldsGridSortProvider } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { DatasourceDownstreamDashboardsQueryGrid } from './DatasourceDownstreamDashboardsQueryGrid';
import { DatasourceDownstreamDatabasesQueryGrid } from './DatasourceDownstreamDatabasesQueryGrid';
import { DatasourceDownstreamDatasourcesQueryGrid } from './DatasourceDownstreamDatasourcesQueryGrid';
import { DatasourceDownstreamFlowsQueryGrid } from './DatasourceDownstreamFlowsQueryGrid';
import { DatasourceDownstreamMetricsQueryGrid } from './DatasourceDownstreamMetricsQueryGrid';
import { DatasourceDownstreamOwnersPane } from './DatasourceDownstreamOwnersPane';
import { DatasourceDownstreamSheetsQueryGrid } from './DatasourceDownstreamSheetsQueryGrid';
import { DatasourceDownstreamTablesQueryGrid } from './DatasourceDownstreamTablesQueryGrid';
import { DatasourceDownstreamWorkbooksQueryGrid } from './DatasourceDownstreamWorkbooksQueryGrid';
import { DatasourceFieldsQueryGrid } from './DatasourceFieldsQueryGrid';
import { DatasourceLineageCounts } from './DatasourceLineageCountsQueryWrapper';
import { DatasourceUpstreamDatabasesQueryGrid } from './DatasourceUpstreamDatabasesQueryGrid';
import { DatasourceUpstreamDatasourcesQueryGrid } from './DatasourceUpstreamDatasourcesQueryGrid';
import { DatasourceUpstreamFlowsQueryGrid } from './DatasourceUpstreamFlowsQueryGrid';
import { DatasourceUpstreamTablesQueryGrid } from './DatasourceUpstreamTablesQueryGrid';
import { DatasourceUpstreamVirtualConnectionTablesQueryGrid } from './DatasourceUpstreamVirtualConnectionTablesQueryGrid';
import { DatasourceUpstreamVirtualConnectionsQueryGrid } from './DatasourceUpstreamVirtualConnectionsQueryGrid';

export type DatasourceLineageItemConfig = LineageItemConfig<DatasourceLineageCounts, DatasourceLineagePaneParams>;
export type DatasourceLineageSection = LineageSection<DatasourceLineageCounts, DatasourceLineagePaneParams>;

export type DatasourceLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly count: number | null;
  readonly hackyUpdateNumber: number;
};

const datasourceUpstreamVirtualConnection: DatasourceLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
  count: counts => counts.upstreamVirtualConnections,
  path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamVirtualConnectionsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatasourceUpstreamVirtualConnectionsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionsGridSortProvider>
    </PaneWithTitle>
  ),
};

const datasourceUpstreamVirtualConnectionTable: DatasourceLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
  messagesForPane: {
    withCount: Messages.UpstreamVirtualConnectionTablesWithCount,
    withoutCount: Messages.UpstreamVirtualConnectionTables
  },
  count: counts => counts.upstreamVirtualConnectionTables,
  path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamVirtualConnectionTablesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionTablesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatasourceUpstreamVirtualConnectionTablesQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionTablesGridSortProvider>
    </PaneWithTitle>
  ),
};

function getDatasourceUpstreamItems(publishedConnectionsLineageEnabled: boolean): ReadonlyArray<DatasourceLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
      count: counts => counts.upstreamDatabases,
      path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamDatabasesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceUpstreamDatabasesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
      count: counts => counts.upstreamTables,
      path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamTablesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceUpstreamTablesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(publishedConnectionsLineageEnabled ? [datasourceUpstreamVirtualConnection, datasourceUpstreamVirtualConnectionTable] : []),
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
      count: counts => counts.upstreamFlows,
      path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceUpstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
      count: counts => counts.upstreamDatasources,
      path: (urls, vizportalUrlId) => urls.datasourceLineageUpstreamDatasourcesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceUpstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      ),
    },
  ];
}
const memoizedGetDatasourceUpstreamItems = defaultMemoize(getDatasourceUpstreamItems);

const DatasourceAnchor: DatasourceLineageItemConfig = {
  lineageItemType: LineageItemType.Datasource,
  messagesForLineage: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
  messagesForPane: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
  count: counts => counts.fields,
  path: (urls, vizportalUrlId) => urls.datasourceLineageFieldsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <FieldsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatasourceFieldsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            selectedKeys={paneParams.selectedKeys}
            updateSelectionForKey={paneParams.updateSelectionForKey}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
            hackyUpdateNumber={paneParams.hackyUpdateNumber}
          />
        }
      </FieldsGridSortProvider>
    </PaneWithTitle>
  ),
};

const datasourceDownstreamMetrics: DatasourceLineageItemConfig = {
  lineageItemType: LineageItemType.Metric,
  messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
  messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
  count: counts => counts.downstreamMetrics,
  path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamMetricsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <MetricsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatasourceDownstreamMetricsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            filteredFieldId={paneParams.filteredFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </MetricsGridSortProvider>
    </PaneWithTitle>
  ),
};

function getDatasourceDownstreamItems(metricsDataCatalogEnabled: boolean): ReadonlyArray<DatasourceLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.DownstreamDatabasesWithCount, withoutCount: Messages.DownstreamDatabases },
      count: counts => counts.downstreamDatabases,
      path: (urls, id) => urls.datasourceLineageDownstreamDatabasesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamDatabasesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.DownstreamTablesWithCount, withoutCount: Messages.DownstreamTables },
      count: counts => counts.downstreamTables,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamTablesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamTablesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamDatasourcesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamWorkbooksPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamWorkbooksQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamSheetsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamSheetsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamDashboardsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatasourceDownstreamDashboardsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredFieldId={paneParams.filteredFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(metricsDataCatalogEnabled ? [datasourceDownstreamMetrics] : []),
  ];
}
const memoizedGetDatasourceDownstreamItems = defaultMemoize(getDatasourceDownstreamItems);

const DatasourceOwners: DatasourceLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, vizportalUrlId) => urls.datasourceLineageDownstreamOwnersPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <DatasourceDownstreamOwnersPane
      title={paneParams.title}
      vizportalUrlId={paneParams.vizportalUrlId}
      filteredFieldId={paneParams.filteredFieldId}
      rowCount={paneParams.count}
    />
  ),
};


function getDatasourceLineageConfiguration(metricsDataCatalogEnabled: boolean, publishedConnectionsLineageEnabled: boolean): ReadonlyArray<DatasourceLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: memoizedGetDatasourceUpstreamItems(publishedConnectionsLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [DatasourceAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetDatasourceDownstreamItems(metricsDataCatalogEnabled),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [DatasourceOwners],
    },
  ];
}
export const memoizedGetDatasourceLineageConfiguration = defaultMemoize(getDatasourceLineageConfiguration);
